import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import RateReviewIcon from "@mui/icons-material/RateReview";
import {Link} from "react-router-dom";
import {
	compareMatchEvents,
	matchEventTime,
	matchEventBelongsToSide,
	participationBeforeEvent,
} from "@/utils/matchEvents";
import {useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";

import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import {MatchAPI} from "@/api/match";

import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SwapVertIcon from "@mui/icons-material/SwapVert";

import {AREA_OPTIONS, getInternalClubId} from "@/utils/constants";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import MainActions from "@/components/MainActions";
import Checkbox from "@mui/material/Checkbox";

import "./DistinteTab.scss";

const EVENT_FILTER_OPTIONS = [
	{value: "all", label: "Tutta la cronaca"},
	{value: "goal", label: "Gol e autogol"},
	{value: "penalty", label: "Rigori"},
	{value: "injury_exit", label: "Uscite per infortunio"},
	{value: "sanction", label: "Sanzioni"},
	{value: "substitution", label: "Sostituzioni"},
];

// ── Role order for sorting ────────────────────────────────────────────────────

const orderMap = AREA_OPTIONS.reduce((acc, role) => {
	acc[role.label] = role.order;
	return acc;
}, {});

const sortByRole = (a, b) =>
	(orderMap[a.player?.primaryRole] ?? 99) -
	(orderMap[b.player?.primaryRole] ?? 99);

// ── Player row ────────────────────────────────────────────────────────────────

export function PlayerRow({
	p,
	onSwap,
	onDelete,
	onUpdate,
	matchData,
	col,
	isInternal,
}) {
	const [editing, setEditing] = useState(false);
	const [number, setNumber] = useState("");
	const [starter, setStarter] = useState(false);
	const [saving, setSaving] = useState(false);
	const [editError, setEditError] = useState("");
	const playerName =
		p.playerName ||
		(p.player
			? `${p.player.firstName || ""} ${p.player.lastName || ""}`.trim()
			: "Nome non indicato");
	const userRole = useSelector((s) => s?.user?.roleName);
	const isStarter = p.startingPlayer;
	const minuteMetric = matchData?.playerStatistics?.find(
		(row) => row.participationId === p.id,
	)?.metrics?.minutes;
	const minutes = p.provisional
		? null
		: minuteMetric
			? minuteMetric.conflict
				? null
				: minuteMetric.value
			: p.calculatedMinutes;
	const dismissal = (matchData?.matchEvents ?? []).find(
		(e) =>
			e.playerId === p.playerId &&
			col &&
			matchEventBelongsToSide(e, matchData, col.side) &&
			["red_card", "double_yellow_card"].includes(e.eventType),
	);
	const benchDismissal =
		dismissal &&
		eventLabel(dismissal, matchData, col).startsWith(
			"Espulsione dalla panchina",
		);

	return (
		<div
			className={`dt-player-row${editing ? " dt-player-row--editing" : ""}`}
			role="group"
			aria-label={`Distinta ${playerName} ${col?.side === "home" ? "casa" : "trasferta"}`}
		>
			<span className="dt-jersey">{p.playerNumber ?? "–"}</span>

			{userRole !== "Analyst" ? (
				<button
					className={`dt-role-chip dt-role-chip--${isStarter ? "tit" : "ris"}`}
					disabled={saving || editing}
					onClick={() => onSwap(p)}
					title={
						p.provisional
							? "Abbina a un giocatore"
							: isStarter
								? "Sposta alle riserve"
								: "Sposta ai titolari"
					}
				>
					{p.provisional ? "?" : isStarter ? "TIT" : "RIS"}
					{p.provisional ? (
						<EditIcon sx={{fontSize: 11}} />
					) : (
						<SwapVertIcon sx={{fontSize: 11}} />
					)}
				</button>
			) : (
				<span
					className={`dt-role-chip dt-role-chip--${isStarter ? "tit" : "ris"}`}
				>
					{p.provisional ? "?" : isStarter ? "TIT" : "RIS"}
				</span>
			)}

			<div className="dt-player-info">
				<span className="dt-player-name">
					{p.playerName ||
						(p.player
							? `${p.player.firstName} ${p.player.lastName}`
							: "Nome non indicato")}
				</span>
				<span className="dt-player-meta">
					{[p.roleSnapshot || p.player?.primaryRole, p.playerYear]
						.filter(Boolean)
						.join(" · ")}
				</span>
				{minutes != null && (
					<span
						className="dt-player-meta"
						title={
							(
								minuteMetric
									? minuteMetric.source === "events"
									: p.calculatedMinutes != null
							)
								? "Calcolati dalla cronaca"
								: "Inseriti a mano"
						}
					>
						{minutes}′ giocati
					</span>
				)}
				{!p.provisional && minuteMetric?.conflict && (
					<span className="dt-player-meta text-danger">
						Minuti diversi tra cronaca e distinta
					</span>
				)}
				{p.dismissed && (
					<span className="dt-player-meta text-danger">
						{benchDismissal ? "Espulsione dalla panchina" : "Espulso"} al{" "}
						{p.dismissalTime}
					</span>
				)}

				<PlayerEventSummary p={p} matchData={matchData} col={col} />
				{p.otherRoster && <span className="dt-player-meta">Altra rosa</span>}
				{p.provisional ? (
					<span className="dt-player-meta">Da verificare</span>
				) : p.startingPlayer ? (
					p.hasPlayed === false && (
						<span className="dt-player-meta">Non ha giocato</span>
					)
				) : (
					<span className="dt-player-meta">
						{p.hasPlayed ? "Entrato" : "Non entrato"}
					</span>
				)}
			</div>

			{isInternal === false &&
				p.playerId &&
				!p.provisional &&
				!["Analyst", "Player"].includes(userRole) &&
				!editing && (
					<Tooltip title="Nuova relazione">
						<Link
							className="dt-icon-btn dt-valuation-btn"
							aria-label={`Nuova relazione ${playerName}`}
							to={`/scouting/relazioni/new?playerId=${encodeURIComponent(p.playerId)}`}
						>
							<RateReviewIcon sx={{fontSize: 18}} />
						</Link>
					</Tooltip>
				)}

			{!["Analyst", "Player"].includes(userRole) &&
				!p.provisional &&
				onUpdate &&
				!editing && (
					<Tooltip title="Modifica numero e titolarità">
						<button
							type="button"
							className="dt-icon-btn dt-edit-btn"
							aria-label={`Modifica distinta ${playerName}`}
							onClick={() => {
								setNumber(p.playerNumber ?? "");
								setStarter(!!p.startingPlayer);
								setEditError("");
								setEditing(true);
							}}
						>
							<EditIcon sx={{fontSize: 17}} />
						</button>
					</Tooltip>
				)}
			{userRole !== "Analyst" && !editing && (
				<button
					className="dt-icon-btn dt-icon-btn--delete"
					disabled={saving}
					onClick={() => onDelete(p)}
				>
					<CloseIcon sx={{fontSize: 14}} />
				</button>
			)}
			{editing && (
				<form
					className="dt-lineup-edit"
					aria-label={`Modifica distinta ${playerName}`}
					onSubmit={async (event) => {
						event.preventDefault();
						const jersey = Number(number);
						if (
							number === "" ||
							!Number.isInteger(jersey) ||
							jersey < 0 ||
							jersey > 999
						) {
							setEditError("Indica un numero di maglia intero da 0 a 999.");
							return;
						}
						setSaving(true);
						setEditError("");
						try {
							const result = await onUpdate(p, {
								playerNumber: jersey,
								startingPlayer: starter,
							});
							if (result?.success) setEditing(false);
							else
								setEditError(
									result?.error || "Impossibile aggiornare la distinta.",
								);
						} finally {
							setSaving(false);
						}
					}}
				>
					<div className="dt-number-field">
						<VLFTextField
							label="N° maglia"
							type="number"
							margin="none"
							value={number}
							disabled={saving}
							onChange={(event) => setNumber(event.target.value)}
							inputProps={{
								"aria-label": `Numero di maglia ${playerName}`,
								min: 0,
								max: 999,
							}}
						/>
					</div>
					<div
						className="dt-proposal__choices"
						role="group"
						aria-label={`Ruolo salvato ${playerName}`}
					>
						<button
							type="button"
							className="dt-choice dt-choice--starter"
							aria-pressed={starter}
							disabled={saving}
							onClick={() => setStarter(true)}
						>
							Titolare
						</button>
						<button
							type="button"
							className="dt-choice dt-choice--bench"
							aria-pressed={!starter}
							disabled={saving}
							onClick={() => setStarter(false)}
						>
							Riserva
						</button>
					</div>
					<div className="dt-lineup-edit__actions">
						<Tooltip title="Salva modifiche">
							<span>
								<button
									type="submit"
									className="dt-icon-btn dt-edit-btn"
									disabled={saving}
									aria-label={`Salva distinta ${playerName}`}
								>
									<SaveIcon sx={{fontSize: 18}} />
								</button>
							</span>
						</Tooltip>
						<Tooltip title="Annulla modifiche">
							<span>
								<button
									type="button"
									className="dt-icon-btn dt-edit-btn"
									disabled={saving}
									aria-label={`Annulla modifica ${playerName}`}
									onClick={() => setEditing(false)}
								>
									<CloseIcon sx={{fontSize: 18}} />
								</button>
							</span>
						</Tooltip>
					</div>
					{editError && (
						<p role="alert" className="dt-lineup-edit__error">
							{editError}
						</p>
					)}
				</form>
			)}
		</div>
	);
}

function PlayerEventSummary({p, matchData, col}) {
	if (!col) return null;
	if (p.provisional) {
		const events = (matchData?.matchEvents ?? []).filter((e) =>
			matchEventBelongsToSide(e, matchData, col.side),
		);
		const counts = [
			[
				"Gol",
				events.filter(
					(e) =>
						["goal", "penalty_scored"].includes(e.eventType) &&
						e.scorerParticipationId === p.id,
				).length,
			],
			[
				"Assist",
				events.filter(
					(e) => e.eventType === "goal" && e.assistParticipationId === p.id,
				).length,
			],
			[
				"Autogol",
				events.filter(
					(e) => e.eventType === "own_goal" && e.scorerParticipationId === p.id,
				).length,
			],
			[
				"Rigori segnati",
				events.filter(
					(e) =>
						e.eventType === "penalty_scored" &&
						e.scorerParticipationId === p.id,
				).length,
			],
			[
				"Rigori sbagliati",
				events.filter(
					(e) =>
						e.eventType === "penalty_missed" &&
						e.scorerParticipationId === p.id,
				).length,
			],
			[
				"Uscite per infortunio",
				events.filter(
					(e) =>
						e.eventType === "injury_exit" && e.scorerParticipationId === p.id,
				).length,
			],
		].filter(([, count]) => count);
		return counts.length ? (
			<span className="dt-player-meta">
				{counts.map(([label, count]) => `${label}: ${count}`).join(" · ")}
			</span>
		) : null;
	}
	if (!p.playerId) return null;
	const events = (matchData?.matchEvents ?? [])
		.filter((e) => matchEventBelongsToSide(e, matchData, col.side))
		.sort(compareMatchEvents);
	const groups = [
		{
			type: "goal",
			label: "Gol",
			events: events.filter(
				(e) =>
					["goal", "penalty_scored"].includes(e.eventType) &&
					e.playerId === p.playerId,
			),
		},
		{
			type: "assist",
			label: "Assist",
			events: events.filter(
				(e) => e.eventType === "goal" && e.assistPlayerId === p.playerId,
			),
		},
		...[
			"own_goal",
			"penalty_scored",
			"penalty_missed",
			"injury_exit",
			"yellow_card",
			"double_yellow_card",
			"red_card",
		].map((type) => ({
			type,
			label: EVENT_LABELS[type],
			events: events.filter(
				(e) => e.eventType === type && e.playerId === p.playerId,
			),
		})),
	].filter((g) => g.events.length);
	if (!groups.length) return null;
	return (
		<div className="dt-player-events" aria-label="Cronaca del giocatore">
			{groups.map((group) => (
				<Tooltip
					key={group.type}
					arrow
					enterTouchDelay={0}
					title={
						<div>
							<strong>
								{group.label}: {group.events.length}
							</strong>
							{group.events.map((e) => (
								<div key={e.id}>
									{matchEventTime(e)}
									{["red_card", "double_yellow_card"].includes(group.type)
										? ` · ${eventLabel(e, matchData, col)}`
										: ""}
								</div>
							))}
						</div>
					}
				>
					<span
						className={`dt-player-event dt-player-event--${group.type}`}
						tabIndex={0}
						aria-label={`${group.label}: ${group.events.length}`}
					>
						{group.type === "assist" ? (
							<span aria-hidden="true" className="dt-player-event__assist">
								👟
							</span>
						) : (
							<TimelineIcon eventType={group.type} />
						)}
						<span>{group.events.length}</span>
					</span>
				</Tooltip>
			))}
		</div>
	);
}

// ── Event row ─────────────────────────────────────────────────────────────────

function EventRow({e, type, label, onDelete, onEdit}) {
	const userRole = useSelector((s) => s?.user?.roleName);

	const playerName = (stored, player) => {
		const name =
			stored || `${player?.firstName ?? ""} ${player?.lastName ?? ""}`.trim();
		return !name ||
			name === "Nome non indicato" ||
			name === "Marcatore sconosciuto"
			? "---"
			: name;
	};
	return (
		<div className="dt-event-row">
			<Tooltip title={label} arrow enterTouchDelay={0}>
				<span className="dt-event-icon" tabIndex={0} aria-label={label}>
					<TimelineIcon eventType={e.eventType} />
				</span>
			</Tooltip>

			<div className="dt-event-info">
				<div className="dt-event-heading">
					<span className="dt-player-name">
						{type === "substitution" ? "Entra: " : ""}#{e.playerNumber ?? "–"}{" "}
						{playerName(e.playerName, e.player)}
					</span>
					<span className="dt-event-time">{matchEventTime(e)}</span>
				</div>
				{type === "substitution" && (
					<span className="dt-player-meta dt-event-secondary">
						Esce: #{e.substitutePlayerNumber ?? "–"}{" "}
						{playerName(e.outgoingPlayerName, e.substitutePlayer)}
					</span>
				)}
				{type === "goal" && (e.assistPlayerId || e.assistParticipationId) && (
					<span className="dt-player-meta dt-event-secondary">
						Assist: #{e.assistPlayerNumber ?? "–"}{" "}
						{playerName(e.assistPlayerName, e.assistPlayer)}
					</span>
				)}
				{e.validationWarning && (
					<span role="alert" className="text-danger">
						{e.validationWarning}
					</span>
				)}
			</div>

			{!["Analyst", "Player"].includes(userRole) && onEdit && (
				<button
					className="dt-icon-btn"
					title="Modifica evento"
					aria-label="Modifica evento"
					onClick={() => onEdit(e)}
				>
					<EditIcon sx={{fontSize: 16}} />
				</button>
			)}
			{!["Analyst", "Player"].includes(userRole) && (
				<button
					className="dt-icon-btn dt-icon-btn--delete"
					title="Elimina evento"
					aria-label="Elimina evento"
					onClick={() => onDelete(e)}
				>
					<CloseIcon sx={{fontSize: 14}} />
				</button>
			)}
		</div>
	);
}

// ── Section ───────────────────────────────────────────────────────────────────

function Section({label, count, onAdd, children}) {
	const userRole = useSelector((s) => s?.user?.roleName);
	const canAdd = !["Analyst", "Player"].includes(userRole) && onAdd;

	return (
		<div className="dt-section">
			<div className="dt-section__header">
				<span className="dt-section__label">{label}</span>
				{count > 0 && <span className="dt-section__count">{count}</span>}
				{canAdd && (
					<Tooltip title="Aggiungi giocatore">
						<button
							type="button"
							className="dt-icon-btn"
							aria-label="Aggiungi giocatore"
							onClick={onAdd}
						>
							<AddIcon sx={{fontSize: 19}} />
						</button>
					</Tooltip>
				)}
			</div>
			<div className="dt-section__body">
				{count > 0
					? children
					: !canAdd && <div className="dt-empty">Nessuno</div>}
			</div>
		</div>
	);
}

export function RosterProposal({col, matchData, onConfirmRoster}) {
	const [roster, setRoster] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [draft, setDraft] = useState({});
	const [reload, setReload] = useState(0);
	const [validation, setValidation] = useState("");
	const [showAll, setShowAll] = useState(false);
	useEffect(() => {
		let active = true;
		setRoster(null);
		setDraft({});
		setError("");
		setValidation("");
		setLoading(true);
		MatchAPI.getLineupRoster(matchData.id, col.side)
			.then((data) => {
				if (active) setRoster(data);
			})
			.catch((err) => {
				if (active)
					setError(
						err?.response?.data?.error || "Impossibile caricare la rosa.",
					);
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [matchData.id, col.side, col.rosterId, reload]);

	const existing = (matchData.matchPlayers || []).filter((p) =>
		p.matchSide
			? p.matchSide === col.side
			: p.clubId === col.clubId &&
				matchData.homeClubId !== matchData.awayClubId,
	);
	const unavailable = (matchData.matchConvocations || []).filter(
		(c) =>
			c.matchSide === col.side && ["Assente", "Infortunato"].includes(c.status),
	);
	const availableCalls = (matchData.matchConvocations || []).filter(
		(c) =>
			c.matchSide === col.side &&
			["Convocato", "convocato", "Presente"].includes(c.status),
	);
	const candidates = (roster?.players || [])
		.filter((p) => !existing.some((e) => e.playerId === p.playerId))
		.sort(
			(a, b) =>
				Number(availableCalls.some((c) => c.playerId === b.playerId)) -
				Number(availableCalls.some((c) => c.playerId === a.playerId)),
		);
	// Per la nostra squadra la distinta parte dai convocati, già proposti come riserve.
	const calledIds = new Set(availableCalls.map((c) => c.playerId));
	const fromCalls =
		col.clubId === getInternalClubId() &&
		candidates.some((p) => calledIds.has(p.playerId));
	const shown =
		fromCalls && !showAll
			? candidates.filter((p) => calledIds.has(p.playerId))
			: candidates;
	const choiceOf = (p) =>
		draft[p.playerId]?.choice ||
		(fromCalls && calledIds.has(p.playerId) ? "bench" : "out");
	const selected = shown.filter(
		(p) =>
			["starter", "bench"].includes(choiceOf(p)) &&
			!unavailable.some((c) => c.playerId === p.playerId),
	);
	const update = (id, key, value) =>
		setDraft((prev) => ({...prev, [id]: {...prev[id], [key]: value}}));
	if (loading)
		return (
			<div className="dt-proposal-note" role="status">
				Caricamento rosa...
			</div>
		);
	return (
		<div className="dt-proposal">
			<div className="dt-proposal__header">
				<strong>
					{roster?.rosterName
						? `Dalla rosa ${roster.rosterName}`
						: "Giocatori della rosa"}
				</strong>
				<Tooltip title="Ricarica rosa">
					<button
						type="button"
						className="dt-icon-btn"
						aria-label="Ricarica rosa"
						disabled={saving}
						onClick={() => setReload((n) => n + 1)}
					>
						<RefreshIcon sx={{fontSize: 17}} />
					</button>
				</Tooltip>
			</div>
			{error ? (
				<p role="alert" className="text-danger dt-proposal-note">
					{error}
				</p>
			) : !roster?.rosterId ? (
				<p className="dt-proposal-note">
					Nessuna rosa associata. Puoi aggiungere i giocatori manualmente.
				</p>
			) : !candidates.length ? (
				<p className="dt-proposal-note">
					{roster.players?.length
						? "Tutti i giocatori della rosa sono già in distinta."
						: "La rosa non contiene giocatori. Puoi aggiungerli manualmente."}
				</p>
			) : (
				<>
					<p className="dt-proposal-note">
						{fromCalls
							? "Convocati: scegli i titolari e i numeri di maglia, poi conferma."
							: "Scegli titolari e riserve, poi conferma. Gli esclusi non vengono inseriti."}
						{fromCalls && candidates.length > shown.length && (
							<button
								type="button"
								className="dt-link-btn"
								onClick={() => setShowAll(true)}
							>
								Mostra anche i non convocati ({candidates.length - shown.length}
								)
							</button>
						)}
						{fromCalls && showAll && (
							<button
								type="button"
								className="dt-link-btn"
								onClick={() => setShowAll(false)}
							>
								Solo i convocati
							</button>
						)}
					</p>
					{validation && (
						<p role="alert" className="text-danger dt-proposal-note">
							{validation}
						</p>
					)}
					<div className="dt-proposal__labels">
						<span>Giocatore</span>
						<span>N°</span>
						<span>In partita</span>
					</div>
					{shown.map((p) => {
						const row = draft[p.playerId] || {};
						const called = fromCalls && calledIds.has(p.playerId);
						const blocked = unavailable.find((c) => c.playerId === p.playerId);
						const partial = existing.some(
							(e) =>
								e.provisional &&
								p.playerNumber != null &&
								e.playerNumber === p.playerNumber,
						);
						return (
							<div className="dt-proposal__row" key={p.playerId}>
								<div>
									<span className="dt-player-name">{p.playerName}</span>
									<span className="dt-player-meta">{p.role}</span>
									{p.sourceRosterId && p.sourceRosterId !== roster.rosterId && (
										<span className="dt-player-meta">Altra rosa</span>
									)}
									{!called &&
										availableCalls.some((c) => c.playerId === p.playerId) && (
											<span className="dt-player-meta dt-proposal__called">
												{availableCalls.find((c) => c.playerId === p.playerId)
													.status === "Presente"
													? "Presente"
													: "Convocato"}
											</span>
										)}
									{blocked && (
										<span className="dt-player-meta text-danger">
											{blocked.status}: aggiorna prima la convocazione
										</span>
									)}
									{partial && (
										<span className="dt-player-meta">
											Numero già presente nella distinta: controlla che sia lo
											stesso giocatore.
										</span>
									)}
								</div>
								<VLFTextField
									type="number"
									margin="none"
									value={row.number ?? p.playerNumber ?? ""}
									disabled={saving || !!blocked}
									onChange={(e) => update(p.playerId, "number", e.target.value)}
									inputProps={{
										"aria-label": `Numero di maglia ${p.playerName}`,
										min: 0,
										max: 999,
									}}
								/>
								<div
									className="dt-proposal__choices"
									role="group"
									aria-label={`Ruolo in partita ${p.playerName}`}
								>
									{[
										{value: "starter", label: "Titolare"},
										{value: "bench", label: "Riserva"},
										...(called ? [] : [{value: "out", label: "Escluso"}]),
									].map((choice) => (
										<button
											type="button"
											key={choice.value}
											disabled={saving || !!blocked}
											aria-pressed={choiceOf(p) === choice.value}
											className={`dt-choice dt-choice--${choice.value}`}
											onClick={() => update(p.playerId, "choice", choice.value)}
										>
											{choice.label}
										</button>
									))}
								</div>
							</div>
						);
					})}
					<div className="dt-proposal__footer">
						<span role="status">
							{selected.filter((p) => choiceOf(p) === "starter").length}{" "}
							titolari ·{" "}
							{selected.filter((p) => choiceOf(p) === "bench").length} riserve
						</span>
						<Tooltip title="Conferma giocatori selezionati">
							<span>
								<button
									type="button"
									className="dt-icon-btn"
									aria-label={`Conferma distinta ${col.club}`}
									disabled={saving || !selected.length}
									onClick={async () => {
										const payload = selected.map((p) => {
											const n = draft[p.playerId]?.number ?? p.playerNumber;
											return {
												playerId: p.playerId,
												startingPlayer: choiceOf(p) === "starter",
												playerNumber: n === "" || n == null ? null : Number(n),
												sourceRosterId: p.sourceRosterId || roster.rosterId,
												playerYear: p.playerYear,
											};
										});
										if (
											payload.some(
												(p) =>
													p.playerNumber != null &&
													(!Number.isInteger(p.playerNumber) ||
														p.playerNumber < 0 ||
														p.playerNumber > 999),
											)
										) {
											setValidation(
												"Indica numeri di maglia interi da 0 a 999.",
											);
											return;
										}
										setSaving(true);
										try {
											if (await onConfirmRoster(col, payload)) {
												setDraft({});
												setValidation("");
											}
										} finally {
											setSaving(false);
										}
									}}
								>
									<SaveIcon sx={{fontSize: 19}} />
								</button>
							</span>
						</Tooltip>
					</div>
				</>
			)}
		</div>
	);
}

// ── Team column ───────────────────────────────────────────────────────────────

function TeamColumn({
	col,
	isInternal,
	matchData,
	onAddPlayer,
	onAddEvent,
	onSwapPlayer,
	onUpdatePlayer,
	onDeletePlayer,
	onDeleteEvent,
	onDownloadPDF,
	onConfirmRoster,
}) {
	const userRole = useSelector((s) => s?.user?.roleName);

	const allPlayers = useMemo(
		() =>
			(matchData?.matchPlayers ?? []).filter((mp) =>
				mp.matchSide
					? mp.matchSide === col.side
					: mp.clubId === col.clubId &&
						matchData?.homeClubId !== matchData?.awayClubId,
			),
		[
			matchData?.matchPlayers,
			col.clubId,
			col.side,
			matchData?.homeClubId,
			matchData?.awayClubId,
		],
	);

	const starters = useMemo(
		() =>
			allPlayers
				.filter((p) => !p.provisional && p.startingPlayer)
				.sort(sortByRole),
		[allPlayers],
	);

	const subs = useMemo(
		() =>
			allPlayers
				.filter((p) => !p.provisional && !p.startingPlayer)
				.sort(sortByRole),
		[allPlayers],
	);

	return (
		<div className="dt-col">
			{/* Team header */}
			<div className="dt-col__header">
				<span className="dt-col__name">{col.club}</span>
				<MainActions
					actions={[
						!["Analyst", "Player"].includes(userRole) && {
							icon: <AddIcon />,
							title: "Aggiungi giocatore",
							variant: "outlined",
							onClick: () => onAddPlayer(col, isInternal),
						},
						userRole !== "Analyst" && {
							icon: <PictureAsPdfIcon />,
							label: "PDF",
							title: "Scarica PDF",
							variant: "outlined",
							onClick: () => onDownloadPDF(col),
						},
					].filter(Boolean)}
				/>
			</div>

			<p className="dt-player-meta dt-player-events-note">
				Le icone mostrano gol, assist e cartellini segnati nella cronaca.
			</p>
			{/* Formazione */}
			<Section label="Formazione" count={allPlayers.length}>
				{allPlayers.some((p) => p.provisional) && (
					<>
						<div className="dt-sub-label">
							Presenze da verificare (distinta parziale)
						</div>
						{allPlayers
							.filter((p) => p.provisional)
							.map((p) => (
								<PlayerRow
									key={p.id}
									p={p}
									col={col}
									isInternal={isInternal}
									matchData={matchData}
									onSwap={onSwapPlayer}
									onUpdate={onUpdatePlayer}
									onDelete={onDeletePlayer}
								/>
							))}
					</>
				)}
				{starters.length > 0 && (
					<>
						<div className="dt-sub-label">Titolari</div>
						{starters.map((p) => (
							<PlayerRow
								key={p.id}
								p={{
									...p,
									otherRoster: Boolean(
										p.sourceRosterId && p.sourceRosterId !== col.rosterId,
									),
								}}
								matchData={matchData}
								col={col}
								isInternal={isInternal}
								onSwap={onSwapPlayer}
								onUpdate={onUpdatePlayer}
								onDelete={onDeletePlayer}
							/>
						))}
					</>
				)}
				{subs.length > 0 && (
					<>
						<div className="dt-sub-label">Riserve</div>
						{subs.map((p) => (
							<PlayerRow
								key={p.id}
								p={{
									...p,
									otherRoster: Boolean(
										p.sourceRosterId && p.sourceRosterId !== col.rosterId,
									),
								}}
								matchData={matchData}
								col={col}
								isInternal={isInternal}
								onSwap={onSwapPlayer}
								onUpdate={onUpdatePlayer}
								onDelete={onDeletePlayer}
							/>
						))}
					</>
				)}
			</Section>
			{!["Analyst", "Player"].includes(userRole) && onConfirmRoster && (
				<RosterProposal
					col={col}
					matchData={matchData}
					onConfirmRoster={onConfirmRoster}
				/>
			)}
		</div>
	);
}

const matchColumns = (matchData) =>
	[
		{
			side: "home",
			rosterId: matchData?.homeRosterId,
			clubId: matchData?.homeClubId,
			club: matchData?.homeTeamName || matchData?.homeClub,
		},
		{
			side: "away",
			rosterId: matchData?.awayRosterId,
			clubId: matchData?.awayClubId,
			club: matchData?.awayTeamName || matchData?.awayClub,
		},
	].filter(
		(col) =>
			!matchData?.participationSides ||
			matchData.participationSides.includes(col.side),
	);

const EVENT_LABELS = {
	goal: "Gol",
	own_goal: "Autogol · punto all’altra squadra",
	penalty_scored: "Rigore segnato",
	penalty_missed: "Rigore sbagliato",
	injury_exit: "Uscita per infortunio",
	yellow_card: "Cartellino giallo",
	double_yellow_card: "Doppio giallo",
	red_card: "Cartellino rosso",
	substitution: "Sostituzione",
};
const eventLabel = (event, matchData, col) => {
	const label =
		event.eventType === "substitution" && event.substitutionReason === "injury"
			? "Sostituzione per infortunio"
			: EVENT_LABELS[event.eventType] || event.eventType;
	if (!col || !["red_card", "double_yellow_card"].includes(event.eventType))
		return label;
	const complete =
		col.side === "home"
			? matchData?.homeParticipationComplete
			: matchData?.awayParticipationComplete;
	const players = (matchData?.matchPlayers ?? []).filter((p) =>
		matchEventBelongsToSide(p, matchData, col.side),
	);
	const events = (matchData?.matchEvents ?? []).filter((e) =>
		matchEventBelongsToSide(e, matchData, col.side),
	);
	if (
		!complete ||
		event.timeConvention !== "period" ||
		event.validationWarning ||
		!players.some((p) => p.playerId === event.playerId) ||
		events.some(
			(e) =>
				compareMatchEvents(e, event) < 0 &&
				(e.timeConvention !== "period" || e.validationWarning),
		)
	)
		return label;
	const {onPitch} = participationBeforeEvent(
		players,
		events.map((e) => ({...e, matchSide: col.side})),
		col.side,
		event,
		event.id,
	);
	return onPitch.has(event.playerId)
		? label
		: `Espulsione dalla panchina · ${label}`;
};

function TimelineIcon({eventType}) {
	if (eventType === "injury_exit")
		return (
			<span className="dt-injury-icon" aria-hidden="true">
				✚
			</span>
		);
	if (eventType === "substitution") return <SwapVertIcon sx={{fontSize: 21}} />;
	if (["own_goal", "penalty_scored", "penalty_missed"].includes(eventType))
		return (
			<span className="dt-scoring-icon" aria-hidden="true">
				{eventType === "own_goal"
					? "AG"
					: eventType === "penalty_scored"
						? "P✓"
						: "P×"}
			</span>
		);
	if (eventType === "goal")
		return <img src={require("@/assets/images/coach/football.png")} alt="" />;
	if (["yellow_card", "double_yellow_card", "red_card"].includes(eventType))
		return (
			<span
				aria-hidden="true"
				className={`match-card-icon match-card-icon--${eventType}`}
			>
				<i />
				{eventType === "double_yellow_card" && <i />}
			</span>
		);
	return <span>•</span>;
}

function EventTimeline({matchData, columns, onEdit}) {
	const timelineRef = useRef(null);
	const [trackWidths, setTrackWidths] = useState({});
	const events = [...(matchData?.matchEvents ?? [])].sort(compareMatchEvents);
	const periods = useMemo(
		() => matchData?.sportingPeriods ?? [],
		[matchData?.sportingPeriods],
	);
	useEffect(() => {
		const element = timelineRef.current;
		if (!element) return;
		const updateWidths = () => {
			const next = {};
			element.querySelectorAll(".match-timeline__track").forEach((track) => {
				next[track.dataset.period] = Math.round(track.clientWidth);
			});
			setTrackWidths((previous) =>
				JSON.stringify(previous) === JSON.stringify(next) ? previous : next,
			);
		};
		const observer = new ResizeObserver(updateWidths);
		observer.observe(element);
		updateWidths();
		return () => observer.disconnect();
	}, [periods]);
	const positioned = events.filter(
		(e) =>
			e.timeConvention === "period" &&
			columns.some((c) => matchEventBelongsToSide(e, matchData, c.side)) &&
			periods.some(
				(p) =>
					p.number === e.fraction &&
					e.minute >= 0 &&
					e.minute <= p.durationMinutes &&
					(e.recoveryMinutes ?? 0) >= 0,
			),
	);
	const layouts = periods.map((period) => {
		const items = positioned.filter((e) => e.fraction === period.number);
		const recovery = Math.max(0, ...items.map((e) => e.recoveryMinutes ?? 0));
		const regularEnd = recovery ? 82 : 100;
		const lanes = {home: [], away: []};
		const markers = items.map((e) => {
			const side = columns.find((c) =>
				matchEventBelongsToSide(e, matchData, c.side),
			).side;
			const position =
				e.recoveryMinutes > 0
					? regularEnd + ((100 - regularEnd) * e.recoveryMinutes) / recovery
					: (regularEnd * e.minute) / period.durationMinutes;
			const width =
				e.assistPlayerId || e.assistParticipationId
					? 82
					: e.eventType === "substitution"
						? 72
						: 56;
			let lane = lanes[side].findIndex((positions) =>
				positions.every(
					(p) =>
						(Math.abs(p.position - position) *
							(trackWidths[period.number] ?? 260)) /
							100 >=
						(p.width + width) / 2 + 2,
				),
			);
			if (lane < 0) {
				lane = lanes[side].length;
				lanes[side].push([]);
			}
			lanes[side][lane].push({position, width});
			return {event: e, side, position, lane, width};
		});
		return {
			period,
			recovery,
			regularEnd,
			markers,
			homeLanes: lanes.home.length,
			awayLanes: lanes.away.length,
		};
	});
	const homeHeight = Math.max(1, ...layouts.map((p) => p.homeLanes)) * 76 + 12;
	const awayHeight = Math.max(1, ...layouts.map((p) => p.awayLanes)) * 76 + 12;
	const omitted = events.length - positioned.length;
	const name = (e, outgoing = false) => {
		const player = outgoing ? e.substitutePlayer : e.player;
		return (
			(outgoing ? e.outgoingPlayerName : e.playerName) ||
			(player
				? `${player.firstName || ""} ${player.lastName || ""}`.trim()
				: "Marcatore sconosciuto")
		);
	};
	const jersey = (event, outgoing = false) => {
		const stored = outgoing ? event.substitutePlayerNumber : event.playerNumber;
		const playerId = outgoing ? event.substitutePlayerId : event.playerId;
		const participationId = outgoing
			? event.outgoingParticipationId
			: event.scorerParticipationId ?? event.participationId;
		return (
			stored ??
			(matchData?.matchPlayers ?? []).find(
				(p) =>
					matchEventBelongsToSide(p, matchData, event.matchSide) &&
					(participationId
						? p.id === participationId
						: Boolean(playerId) && p.playerId === playerId),
			)?.playerNumber ??
			"–"
		);
	};
	return (
		<section
			ref={timelineRef}
			className="match-timeline"
			aria-label="Andamento della partita"
		>
			<h3>Andamento della partita</h3>
			<div className="match-timeline__legend">
				{columns.map((c) => (
					<span
						key={c.side}
						className={`match-timeline__team match-timeline__team--${c.side}`}
					>
						{c.side === "home" ? "Casa" : "Trasferta"}: {c.club}
					</span>
				))}
			</div>
			{periods.length ? (
				<div
					className="match-timeline__scroll"
					tabIndex={0}
					aria-label="Scorri la cronaca"
				>
					<div
						className={`match-timeline__periods${periods.length > 2 ? " match-timeline__periods--extended" : ""}`}
					>
						{layouts.map(({period, recovery, regularEnd, markers}) => {
							return (
								<div key={period.number} className="match-timeline__period">
									<div className="match-timeline__period-title">
										{period.number}° tempo
										{period.isExtraTime ? " supplementare" : ""} ·{" "}
										{period.durationMinutes}′
										{recovery > 0 && <span> + recupero</span>}
									</div>
									<div
										className="match-timeline__track"
										data-period={period.number}
										style={{height: homeHeight + awayHeight + 30}}
									>
										<div
											className="match-timeline__axis"
											style={{top: homeHeight}}
										/>
										{recovery > 0 && (
											<div
												className="match-timeline__recovery"
												style={{
													left: `${regularEnd}%`,
													top: homeHeight - 5,
													width: `${100 - regularEnd}%`,
												}}
											/>
										)}
										{[
											{minute: 0, position: 0},
											{
												minute: Math.floor(period.durationMinutes / 2),
												position:
													(regularEnd *
														Math.floor(period.durationMinutes / 2)) /
													period.durationMinutes,
											},
											{minute: period.durationMinutes, position: regularEnd},
										].map((tick) => (
											<span
												key={tick.minute}
												className="match-timeline__tick"
												style={{left: `${tick.position}%`, top: homeHeight + 8}}
											>
												{tick.minute}′
											</span>
										))}
										<svg
											className="match-timeline__connections"
											width="100%"
											height={homeHeight + awayHeight + 30}
											aria-hidden="true"
										>
											{markers.map((marker) => {
												const trackWidth = trackWidths[period.number] ?? 260;
												const x = (marker.position * trackWidth) / 100;
												const isHome = marker.side === "home";
												const top = isHome
													? homeHeight - 74 - marker.lane * 76
													: homeHeight + 36 + marker.lane * 76;
												const edge = isHome ? top + 68 : top;
												const obstacles = markers
													.filter(
														(other) =>
															other.side === marker.side &&
															other.lane < marker.lane,
													)
													.map((other) => ({
														left:
															(other.position * trackWidth) / 100 -
															other.width / 2 -
															6,
														right:
															(other.position * trackWidth) / 100 +
															other.width / 2 +
															6,
													}));
												const candidates = [
													x,
													...obstacles.flatMap((o) => [
														o.left - 1,
														o.right + 1,
													]),
												];
												const clear = candidates.filter((candidate) =>
													obstacles.every(
														(o) => candidate < o.left || candidate > o.right,
													),
												);
												const withinTrack = clear.filter(
													(candidate) =>
														candidate >= 0 && candidate <= trackWidth,
												);
												const routeX =
													(withinTrack.length ? withinTrack : clear).sort(
														(a, b) => Math.abs(a - x) - Math.abs(b - x),
													)[0] ?? x;
												const nearAxis = isHome
													? homeHeight - 3
													: homeHeight + 29;
												const nearCard = isHome ? edge + 4 : edge - 4;
												return (
													<g
														key={marker.event.id}
														className={`match-timeline__connection--${marker.side}`}
													>
														<path
															d={`M ${x} ${homeHeight + 1} V ${nearAxis} H ${routeX} V ${nearCard} H ${x} V ${edge}`}
														/>
														<circle cx={x} cy={homeHeight + 1} r="3" />
													</g>
												);
											})}
										</svg>
										{markers.map(({event: e, side, position, lane, width}) => {
											const col = columns.find((c) => c.side === side);
											const label = `${eventLabel(e, matchData, col)} · ${matchEventTime(e)} · ${side === "home" ? "Casa" : "Trasferta"}: ${col.club}`;
											const detail =
												e.eventType === "substitution"
													? `Entra: #${jersey(e)} ${name(e)} · Esce: #${jersey(e, true)} ${name(e, true)}`
													: `#${jersey(e)} ${name(e)}${e.assistPlayerId || e.assistParticipationId ? ` · Assist: #${e.assistPlayerNumber ?? "–"} ${e.assistPlayerName || `${e.assistPlayer?.firstName ?? ""} ${e.assistPlayer?.lastName ?? ""}`}` : ""}`;
											return (
												<Tooltip
													key={e.id}
													arrow
													enterTouchDelay={0}
													title={
														<div>
															<strong>{label}</strong>
															<div>{detail}</div>
															{e.notes && <div>{e.notes}</div>}
														</div>
													}
												>
													<button
														type="button"
														className={`match-timeline__event match-timeline__event--${side}`}
														aria-label={`${onEdit ? "Modifica evento. " : ""}${label}. ${detail}`}
														onClick={onEdit ? () => onEdit(e, col) : undefined}
														aria-haspopup={onEdit ? "dialog" : undefined}
														style={{
															left: `${position}%`,
															width,
															top:
																side === "home"
																	? homeHeight - 74 - lane * 76
																	: homeHeight + 36 + lane * 76,
														}}
													>
														<span className="match-timeline__icons">
															<span className="match-timeline__player">
																<TimelineIcon eventType={e.eventType} />
																<span className="match-timeline__numbers">
																	{e.eventType === "substitution" ? (
																		<>
																			<span className="match-timeline__incoming">
																				↑#{jersey(e)}
																			</span>{" "}
																			<span className="match-timeline__outgoing">
																				↓#{jersey(e, true)}
																			</span>
																		</>
																	) : (
																		<>#{jersey(e)}</>
																	)}
																</span>
															</span>
															{(e.assistPlayerId ||
																e.assistParticipationId) && (
																<span
																	className="match-timeline__assist"
																	title="Assist"
																>
																	<span
																		className="match-timeline__assist-icon"
																		aria-hidden="true"
																	>
																		👟
																	</span>
																	#{e.assistPlayerNumber ?? "–"}
																</span>
															)}
														</span>
														<span>
															{e.minute}′
															{e.recoveryMinutes > 0
																? `+${e.recoveryMinutes}`
																: ""}
														</span>
													</button>
												</Tooltip>
											);
										})}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			) : (
				<p className="dt-player-meta">
					Configura i tempi della partita per visualizzare la timeline.
				</p>
			)}
			{omitted > 0 && (
				<p className="match-timeline__notice">
					{omitted} eventi con tempi o squadra da verificare: disponibili nel
					dettaglio sotto, senza posizionamento automatico.
				</p>
			)}
			{!events.length && <div className="dt-empty">Cronaca vuota</div>}
		</section>
	);
}

export function MatchChronology({
	matchData,
	onDataCompletenessChange,
	onAddEvent,
	onEditEvent,
	onDeleteEvent,
}) {
	const columns = matchColumns(matchData);
	const [filter, setFilter] = useState("all");
	const userRole = useSelector((s) => s?.user?.roleName);
	const editEvent = (event, col) =>
		onEditEvent(
			event,
			col,
			(matchData.matchPlayers ?? []).filter((p) =>
				matchEventBelongsToSide(p, matchData, col.side),
			),
		);
	const events = [...(matchData?.matchEvents ?? [])]
		.sort(compareMatchEvents)
		.filter(
			(e) =>
				filter === "all" ||
				(filter === "sanction"
					? ["yellow_card", "double_yellow_card", "red_card"].includes(
							e.eventType,
						)
					: filter === "goal"
						? ["goal", "penalty_scored", "own_goal"].includes(e.eventType)
						: filter === "penalty"
							? ["penalty_scored", "penalty_missed"].includes(e.eventType)
							: e.eventType === filter),
		);
	const renderEvent = (e, col) => (
		<div className="dt-chronology-entry" key={e.id}>
			<EventRow
				e={e}
				label={eventLabel(e, matchData, col)}
				type={
					["goal", "own_goal", "penalty_scored", "penalty_missed"].includes(
						e.eventType,
					)
						? "goal"
						: e.eventType === "substitution"
							? "substitution"
							: "sanction"
				}
				onDelete={onDeleteEvent}
				onEdit={col ? (event) => editEvent(event, col) : undefined}
			/>
		</div>
	);
	const unassignedEvents = events.filter(
		(e) =>
			!columns.some((col) => matchEventBelongsToSide(e, matchData, col.side)),
	);

	return (
		<div className="match-event-sections">
			<EventTimeline
				matchData={matchData}
				columns={columns}
				onEdit={
					onEditEvent && !["Analyst", "Player"].includes(userRole)
						? editEvent
						: undefined
				}
			/>
			<div className="dt-chronology">
				<h3>Cronaca</h3>
				<p className="dt-player-meta">
					La presenza di eventi non certifica la completezza delle statistiche.
				</p>
				<div className="dt-chronology-toolbar">
					<div className="dt-chronology-filter">
						<VLFSelectNew
							aria-label="Filtro cronaca"
							options={EVENT_FILTER_OPTIONS}
							value={EVENT_FILTER_OPTIONS.find((o) => o.value === filter)}
							onChange={(option) => setFilter(option.value)}
							sorted={false}
							isClearable={false}
							noMargin
						/>
					</div>
				</div>
				<div className="dt-chronology-sides">
					{columns.map((col) => {
						const sideEvents = events.filter((e) =>
							matchEventBelongsToSide(e, matchData, col.side),
						);
						const presenceComplete = Boolean(
							matchData?.[`${col.side}ParticipationComplete`],
						);
						const scoringComplete = Boolean(
							matchData?.[`${col.side}ScoringComplete`],
						);
						const disciplineComplete = Boolean(
							matchData?.[`${col.side}DisciplineComplete`],
						);
						const complete =
							presenceComplete && scoringComplete && disciplineComplete;
						const resultGoalsComplete =
							scoringComplete ||
							Boolean(matchData?.[`${col.side}ResultGoalsComplete`]);
						const confirmed = complete || resultGoalsComplete;
						const someComplete =
							presenceComplete ||
							scoringComplete ||
							disciplineComplete ||
							resultGoalsComplete;
						const hasLineup = (matchData?.matchPlayers ?? []).some((p) =>
							matchEventBelongsToSide(p, matchData, col.side),
						);
						const hasEvents = (matchData?.matchEvents ?? []).some((e) =>
							matchEventBelongsToSide(e, matchData, col.side),
						);
						const state = complete
							? "complete"
							: hasLineup || hasEvents || someComplete
								? "partial"
								: "missing";
						const pending = [
							!presenceComplete && "presenze e cambi",
							!scoringComplete && "goal e assist",
							!disciplineComplete && "cartellini",
						].filter(Boolean);
						const detail = complete
							? "Distinta, presenze e cronaca confermate."
							: resultGoalsComplete
								? "Gol confermati per il risultato. Distinta, presenze e statistiche individuali ancora da verificare."
								: !hasLineup
									? "Distinta da compilare. Dati della squadra ancora da completare."
									: `Dati presenti. Da confermare: ${pending.join(", ")}.`;
						return (
							<section
								key={col.side}
								className={`dt-chronology-side dt-chronology-side--${col.side}`}
								aria-label={`Eventi ${col.side === "home" ? "casa" : "trasferta"}`}
							>
								<div className="dt-chronology-side__header">
									<h4>
										{col.side === "home" ? "Casa" : "Trasferta"}: {col.club}
									</h4>
									{!["Analyst", "Player"].includes(userRole) && (
										<div className="dt-chronology-side__actions">
											{["goal", "sanction", "substitution", "injury_exit"].map(
												(type) => (
													<button
														key={type}
														className="dt-icon-btn"
														title={`Aggiungi ${type === "sanction" ? "sanzione" : type === "substitution" ? "sostituzione" : type === "injury_exit" ? "uscita per infortunio" : "goal, autogol o rigore"} — ${col.club}`}
														aria-label={`Aggiungi ${type} ${col.side}`}
														onClick={() =>
															onAddEvent(
																type,
																col,
																(matchData.matchPlayers ?? []).filter((p) =>
																	matchEventBelongsToSide(
																		p,
																		matchData,
																		col.side,
																	),
																),
															)
														}
													>
														{type === "substitution" ? (
															<SwapVertIcon />
														) : type === "injury_exit" ? (
															<TimelineIcon eventType="injury_exit" />
														) : type === "goal" ? (
															<img
																src={require("@/assets/images/coach/football.png")}
																alt=""
																width="18"
															/>
														) : (
															<TimelineIcon eventType="yellow_card" />
														)}
													</button>
												),
											)}
										</div>
									)}
								</div>
								<div className="dt-event-completeness">
									<label>
										<Checkbox
											size="small"
											checked={confirmed}
											indeterminate={someComplete && !confirmed}
											disabled={["Analyst", "Player"].includes(userRole)}
											onChange={(e) =>
												onDataCompletenessChange(col.side, e.target.checked)
											}
										/>
										Dati della partita completi
									</label>
									<span
										role="status"
										className={`dt-data-status dt-data-status--${state}`}
									>
										{state === "complete"
											? "Dati completi"
											: state === "partial"
												? "Dati parziali"
												: "Dati mancanti"}
									</span>
									<p className="dt-player-meta">{detail}</p>
								</div>
								{sideEvents.length ? (
									sideEvents.map((e) => renderEvent(e, col))
								) : (
									<div className="dt-empty">Nessun evento</div>
								)}
							</section>
						);
					})}
				</div>
				{unassignedEvents.length > 0 && (
					<section
						className="dt-chronology-unassigned"
						aria-label="Azioni senza squadra"
					>
						<h4>Squadra da assegnare</h4>
						{unassignedEvents.map((e) => renderEvent(e))}
					</section>
				)}
			</div>
		</div>
	);
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function DistinteTab({
	matchData,
	clubs,
	onAssignPlayer,
	onConfirmRoster,
	onAddPlayer, // (col, isInternal) → void
	onUpdatePlayer,
	onSwapPlayer, // (p) → void
	onDeletePlayer, // (p) → void
	onDownloadPDF, // (col) → void
}) {
	const columns = matchColumns(matchData);

	return (
		<div className="dt-wrap">
			{matchData?.homeClubId === matchData?.awayClubId &&
				(matchData?.matchPlayers ?? [])
					.filter((p) => !p.matchSide)
					.map((p) => (
						<div className="dt-col" key={p.id}>
							<span>
								{p.playerName ||
									`${p.player?.firstName || ""} ${p.player?.lastName || ""}`}{" "}
								— lato da assegnare
							</span>
							{columns.map((col) => (
								<button
									className="dt-icon-btn"
									key={col.side}
									title={`Assegna a ${col.club}`}
									aria-label={`Assegna a ${col.club}`}
									onClick={() => onAssignPlayer(p, col.side)}
								>
									{col.side === "home" ? (
										<ArrowDownwardIcon />
									) : (
										<ArrowUpwardIcon />
									)}
								</button>
							))}
						</div>
					))}
			{columns.map((col) => {
				const isInternal =
					col.clubId === getInternalClubId() ||
					(clubs?.find((c) => c.id === col.clubId)?.internal ?? false);
				return (
					<TeamColumn
						key={col.side}
						col={col}
						isInternal={isInternal}
						matchData={matchData}
						onAddPlayer={onAddPlayer}
						onConfirmRoster={onConfirmRoster}
						onSwapPlayer={onSwapPlayer}
						onUpdatePlayer={onUpdatePlayer}
						onDeletePlayer={onDeletePlayer}
						onDownloadPDF={onDownloadPDF}
					/>
				);
			})}
		</div>
	);
}
